// ─────────────────────────────────────────────────────────────────────────────
// block_renderer.ts — Canvas 2D renderer for notebook blocks
//
// Sits between the InkLayer (below) and the SpellLayer (above).
// Renders:
//   • InkBlock:     dotted selection bbox only
//   • LatexBlock:   semi-transparent overlay (suggestion) or editable mode
//   • CodeBlock:    arcane void border + dark bg + output area
//   • ArcaneBlock:  heavy void border + rotating alchemical glyphs + state glow
//   • TextBlock:    parchment text rendering
//   • Selection:    8 diamond handles + rotation circle, in spell-family colour
//   • Marginalia:   wizard response notes in the page margin
// ─────────────────────────────────────────────────────────────────────────────

import type { WvBlock, WvMarginNote, WvSelection } from './notebook_state';
import { computeHandles } from './notebook_state';
import { SelectionPolygon } from './selection_polygon';
import { getLatexOverlay } from './latex_overlay';

// ── Naturalisation helpers ───────────────────────────────────────────────────
// Adds a subtle hand-drawn feel to parametric shapes.  Deterministic per-shape
// so the wobble doesn't jitter across frames.

/** Simple deterministic hash → 0..1.  Input: two floats. */
function stableHash(a: number, b: number): number {
  let h = (a * 2654435761 + b * 40503) | 0;
  h = ((h >> 16) ^ h) * 0x45d9f3b | 0;
  h = ((h >> 16) ^ h) * 0x45d9f3b | 0;
  return ((h >> 16) ^ h & 0x7fff) / 0x7fff;
}

/** Perpendicular wobble amplitude — fraction of line length, tiny. */
const WOBBLE_AMP = 0.012;   // 1.2% of edge length
const WOBBLE_STEPS = 5;     // how many intermediary knots on a straight edge

/**
 * Draw a straight edge with subtle hand-drawn wobble.
 * Wobble is perpendicular to the line, deterministic from endpoints.
 */
function strokeNaturalLine(
  ctx: CanvasRenderingContext2D,
  ax: number, ay: number,
  bx: number, by: number,
  scale: number,
): void {
  const dx = bx - ax, dy = by - ay;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 2) { ctx.moveTo(ax, ay); ctx.lineTo(bx, by); return; }
  // Perpendicular unit vector
  const nx = -dy / len, ny = dx / len;
  const amp = Math.min(len * WOBBLE_AMP, 1.8 * scale); // cap wobble at ~1.8px

  ctx.moveTo(ax, ay);
  for (let i = 1; i < WOBBLE_STEPS; i++) {
    const t = i / WOBBLE_STEPS;
    const offset = (stableHash(ax + i * 7.1, ay + i * 3.3) - 0.5) * 2 * amp;
    ctx.lineTo(ax + dx * t + nx * offset, ay + dy * t + ny * offset);
  }
  ctx.lineTo(bx, by);
}

/**
 * Draw a vertex dot with subtle shape irregularity.
 * Instead of a perfect circle, draws a slightly deformed ellipse.
 */
function drawNaturalDot(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  r: number,
): void {
  // Deform radius slightly in X/Y based on position hash
  const h = stableHash(x, y);
  const rx = r * (0.92 + h * 0.16);     // 0.92–1.08
  const ry = r * (1.08 - h * 0.16);     // 0.92–1.08
  const rot = h * 0.5 - 0.25;           // slight tilt ±0.25 rad
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2);
}

// Relation type → visual style
const RELATION_STYLE: Record<string, { color: string; dash: number[]; label: string }> = {
  derives:      { color: 'rgba(100, 200, 140, 0.65)', dash: [],     label: '⟶' },
  implies:      { color: 'rgba(140, 180, 220, 0.65)', dash: [],     label: '⇒' },
  contradicts:  { color: 'rgba(220, 80, 80, 0.65)',   dash: [6, 4], label: '⊥' },
  references:   { color: 'rgba(180, 160, 120, 0.5)',  dash: [3, 3], label: '↗' },
  annotates:    { color: 'rgba(160, 140, 200, 0.5)',  dash: [2, 4], label: '✎' },
  bound:        { color: 'rgba(200, 140, 60, 0.6)',   dash: [],     label: '∮' },
  contains:     { color: 'rgba(100, 100, 100, 0.4)',  dash: [4, 4], label: '⊂' },
  precedes:     { color: 'rgba(120, 160, 100, 0.5)',  dash: [6, 2], label: '≺' },
};

/** Lightweight arrow data passed to BlockRenderer. */
export interface WvRelationArrow {
  id: string;
  fromBbox: { x: number; y: number; width: number; height: number };
  toBbox:   { x: number; y: number; width: number; height: number };
  type: string;
  confirmed: boolean;
  label?: string;
}

// Alchemical / runic glyphs for Arcane void borders
const ARCANE_GLYPHS = ['☽', '☿', '♄', '♃', '♂', '♀', '☉', '☊', '☋', '⊕', '⊗', '∞', '☍', '✦', '✧', '⬡', '⬢', '∯', '⁂', '⍟'];

// Spell family accent colours (must match spell_layer.ts)
const FAMILY_COLORS: Record<string, string> = {
  act: '#e8a020',
  bind: '#6a9fc0',
  transmute: '#b05fd0',
  invoke: '#50c87a',
};

// Arcane state glow colours
const ARCANE_STATE_GLOW: Record<string, string> = {
  sealed: 'rgba(80, 80, 100, 0.25)',
  open: 'rgba(100, 160, 240, 0.55)',
  active: 'rgba(80, 240, 140, 0.70)',
  error: 'rgba(220, 60, 60, 0.65)',
};

const VOID_DARK = '#0e0c12';
const VOID_GAP = 14;         // px void border thickness
const ARCANE_GLYPH_SIZE = 10;
const CODE_VOID_GAP = 10;

// ─────────────────────────────────────────────────────────────────────────────

export class BlockRenderer {
  private _canvas: HTMLCanvasElement;
  /** Optional ink layer reference. When set, figures with a
   *  `renderPoints + inkStyle` envelope (Batch 6) route their body
   *  through the same brush dispatch as live ink — preserves the
   *  source stroke's pressure / tilt feel on top of the clean
   *  parametric geometry. Heads / dots / handles still draw via
   *  this renderer. */
  private _inkLayer: import('./ink_layer').InkLayer | null = null;
  /** Set by caller each frame to avoid per-block Date.now() calls. */
  frameNow = 0;

  /**
   * Per-frame subpage-arrow hit rects.  Cleared at the start of every draw,
   * populated by the markdown-text renderer for any block that paginates.
   * CanvasApp's hit-rect rebuild reads this after the frame.
   */
  subpageHitRects: Array<{
    blockId: string;
    dir: -1 | 1;
    x: number; y: number; w: number; h: number;
  }> = [];

  /** Per-frame layout of the selection modifier toolbar — pill rects
   *  for lock / group toggles. Page-local coordinates. CanvasApp
   *  hit-tests these in its pointer-down path. Empty when no
   *  selection. Astral stays internal and isn't user-facing here. */
  modifierStripHits: Array<{
    kind: 'lock' | 'group';
    active: boolean;
    x: number; y: number; w: number; h: number;
  }> = [];

  /** Mirror of notebook_state.lockFocus — set per-frame by the caller
   *  so the renderer can fade inert content without reaching into state. */
  lockFocus = false;
  private get _lockFocus(): boolean { return this.lockFocus; }

  // ── Selection animation ────────────────────────────────────────────────
  private _selPoly = new SelectionPolygon();
  private _selBirthSeen = 0;

  constructor(canvas: HTMLCanvasElement) {
    this._canvas = canvas;
  }

  /** Wire in the ink layer so figure bodies can render through the
   *  same brush dispatch as live ink. Call once after the canvas
   *  app has constructed both. Without this hook, figures fall back
   *  to the legacy clean-vector rendering (still works, just loses
   *  the source-stroke feel). */
  setInkLayer(layer: import('./ink_layer').InkLayer): void {
    this._inkLayer = layer;
  }

  /**
   * Draws all block decorations. Call each animation frame, after ink layer.
   * Assumes the canvas context has been translated to page-local coordinates
   * (i.e. ctx.translate(layout.x, layout.y) is already applied by the caller).
   */
  draw(
    blocks: WvBlock[],
    selection: WvSelection,
    notes: WvMarginNote[],
    ctx: CanvasRenderingContext2D,
    t: number,    // animation time in seconds (performance.now() / 1000)
    arrows: WvRelationArrow[] = [],
  ): void {
    // Reset per-frame subpage hit rects; each visible block re-adds its own.
    this.subpageHitRects.length = 0;
    // Modifier strip rebuilt each frame from the current selection.
    this.modifierStripHits.length = 0;

    for (const block of blocks) {
      this._drawBlock(block, selection, ctx, t);
    }

    // Relation arrows (drawn between blocks, under selection handles)
    for (const arrow of arrows) {
      this._drawRelationArrow(arrow, ctx, t);
    }

    // Selection handles drawn last (always on top of block decoration)
    // Selection is content-only by invariant — the handle frame is just
    // the merged bbox over the selected blocks. No region / strokeIds
    // fallback: the reducer rejects empty selections so we always have
    // at least one block to bound.
    if (selection.blockIds.size > 0) {
      // If this is a new selection, reset polygon
      if (selection.birth > 0 && selection.birth !== this._selBirthSeen) {
        this._selBirthSeen = selection.birth;
        this._selPoly.clear();
      }

      // Compute merged bbox over every selected block.
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const blockId of selection.blockIds) {
        const block = blocks.find(b => b.id === blockId);
        if (!block) continue;
        minX = Math.min(minX, block.bbox.x);
        minY = Math.min(minY, block.bbox.y);
        maxX = Math.max(maxX, block.bbox.x + block.bbox.width);
        maxY = Math.max(maxY, block.bbox.y + block.bbox.height);
      }

      if (minX < Infinity) {
        const mergedBbox = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
        const handles = computeHandles(mergedBbox, 10);
        this._selPoly.reshape(handles);
        this._selPoly.tick(performance.now());
        this._selPoly.draw(ctx);

        // Modifier strip — lock / group / astral toggles, drawn just
        // below the selection bbox so it doesn't collide with the
        // delete handle (which sits above the top edge).
        this._drawModifierStrip(ctx, mergedBbox, selection, blocks);
      }
    } else {
      // No selection — reset animation state
      this._selPoly.clear();
      this._selBirthSeen = 0;
    }

    // Marginalia
    for (const note of notes) { this._drawMarginale(note, ctx, t); }
  }

  // ── Per-block dispatch ────────────────────────────────────────────────────

  private _drawBlock(
    block: WvBlock,
    sel: WvSelection,
    ctx: CanvasRenderingContext2D,
    t: number,
  ): void {
    const selected = sel.blockIds.has(block.id);
    const rot = block.rotation ?? 0;
    if (rot !== 0) {
      const cx = block.bbox.x + block.bbox.width / 2;
      const cy = block.bbox.y + block.bbox.height / 2;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);
      ctx.translate(-cx, -cy);
    }
    // Fade non-interactive content under the current focus mode. Locked
    // items fade in normal mode; unlocked items fade when the user
    // toggles into "lock focus" so locked decorations move to the front.
    const inert = this._lockFocus
      ? !(block as import('./notebook_state').WvBaseBlock).locked
      : !!(block as import('./notebook_state').WvBaseBlock).locked && !selected;
    if (inert) {
      ctx.save();
      ctx.globalAlpha = 0.35;
    }
    switch (block.kind) {
      case 'ink':    this._drawInkBlock(block.bbox, selected, ctx); break;
      case 'latex':  this._drawLatexBlock(block, selected, ctx, t); break;
      case 'code':   this._drawCodeBlock(block, selected, ctx, t); break;
      case 'arcane': this._drawArcaneBlock(block, selected, ctx, t); break;
      case 'text':   this._drawTextBlock(block, selected, ctx); break;
      case 'figure': this._drawFigureBlock(block as import('./notebook_state').WvFigureBlock, selected, ctx); break;
    }
    if (inert) { ctx.restore(); }
    if (rot !== 0) { ctx.restore(); }
  }

  // ── Ink block ──────────────────────────────────────────────────────────────
  // Invisible unless selected — ink strokes ARE the visual.

  private _drawInkBlock(
    bbox: { x: number; y: number; width: number; height: number },
    selected: boolean,
    ctx: CanvasRenderingContext2D,
  ): void {
    // Selection is shown by the particle polygon handles, no dashed frame
  }

  // ── Figure block ───────────────────────────────────────────────────────────
  // Clean anti-aliased vector shape replacing hand-drawn ink.

  private _drawFigureBlock(
    block: import('./notebook_state').WvFigureBlock,
    selected: boolean,
    ctx: CanvasRenderingContext2D,
  ): void {
    const STROKE_COLOR = '#32281e';
    const POINT_FILL = '#584838';
    const POINT_BORDER = '#1a1208';
    // Scale rendering properties from the pen size at creation time.
    // Default pen size is 1.5 (DEFAULT_INK_STYLE.size).
    const ps = block.penSize ?? 1.5;
    const scale = ps / 1.5;
    const VERTEX_RADIUS = 1.9 * scale;
    const LINE_WIDTH = 1.35 * scale;
    const ARROW_HEAD = 6 * scale;
    const POINT_RADIUS = VERTEX_RADIUS;

    ctx.save();
    ctx.strokeStyle = STROKE_COLOR;
    ctx.fillStyle = STROKE_COLOR;
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Apply line style dash pattern
    if (block.lineStyle === 'dashed') {
      ctx.setLineDash([6 * scale, 4 * scale]);
    } else if (block.lineStyle === 'dotted') {
      ctx.setLineDash([1.5 * scale, 3 * scale]);
    }

    const verts = block.vertices;

    // Batch 6: when the figure carries a kinematic envelope + style
    // snapshot from its source stroke, render the body through the
    // ink layer so it inherits the original brush feel. Heads /
    // dots / handles are still drawn below by this renderer (they're
    // interaction visuals, not ink).
    let bodyHandledByInk = false;
    if (this._inkLayer
        && block.renderPoints && block.renderPoints.length >= 2
        && block.inkStyle
        && block.shapeKind !== 'point') {
      this._inkLayer.renderShapeStroke(ctx, block.renderPoints, block.inkStyle);
      bodyHandledByInk = true;
    }

    // Point (single filled dot with border — natural shape)
    if (block.shapeKind === 'point' && block.center) {
      drawNaturalDot(ctx, block.center.x, block.center.y, POINT_RADIUS);
      ctx.fillStyle = POINT_FILL;
      ctx.fill();
      ctx.strokeStyle = POINT_BORDER;
      ctx.lineWidth = 0.6;
      ctx.stroke();
      ctx.strokeStyle = STROKE_COLOR;
      ctx.lineWidth = LINE_WIDTH;
    // Circle / Ellipse (with subtle wobble) — body draws here ONLY if
    // ink layer didn't already render it via the kinematic envelope.
    } else if (!bodyHandledByInk
        && (block.shapeKind === 'circle' || block.shapeKind === 'ellipse')
        && block.center) {
      const cx = block.center.x;
      const cy = block.center.y;
      const rx = block.rx ?? 1;
      const ry = block.ry ?? rx;
      // Draw with slight radial wobble for naturalism
      const N_ARC = 48;
      const h0 = stableHash(cx, cy);
      const wobAmp = Math.min(Math.max(rx, ry) * 0.008, 1.0 * scale);
      ctx.beginPath();
      for (let i = 0; i <= N_ARC; i++) {
        const a = (i / N_ARC) * Math.PI * 2;
        const w = (stableHash(cx + i * 5.7, cy + i * 3.1 + h0 * 100) - 0.5) * 2 * wobAmp;
        const px = cx + (rx + w) * Math.cos(a);
        const py = cy + (ry + w) * Math.sin(a);
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();
    } else {
      // Edges (line segments, arrows, curves). When the body was
      // already rendered via the ink layer we still walk edges to
      // draw arrow heads — the head is an interaction visual that
      // doesn't belong on the kinematic envelope.
      for (const edge of block.edges) {
        const a = verts[edge.from];
        const b = verts[edge.to];
        if (!a || !b) { continue; }

        if (!bodyHandledByInk) {
          ctx.beginPath();
          if (edge.controlPoints && edge.controlPoints.length >= 1) {
            // Draw curve path for any edge with control points (curves & curved arrows)
            ctx.moveTo(a.x, a.y);
            const cp = edge.controlPoints;
            if (cp.length === 1) {
              ctx.quadraticCurveTo(cp[0].x, cp[0].y, b.x, b.y);
            } else {
              ctx.bezierCurveTo(cp[0].x, cp[0].y, cp[1].x, cp[1].y, b.x, b.y);
            }
          } else {
            // Natural wobble on straight edges
            strokeNaturalLine(ctx, a.x, a.y, b.x, b.y, scale);
          }
          ctx.stroke();
        }

        // Arrow head
        if (edge.type === 'arrow') {
          let angle: number;
          if (edge.controlPoints && edge.controlPoints.length >= 1) {
            // Use tangent at endpoint for curved arrows
            const lastCp = edge.controlPoints[edge.controlPoints.length - 1];
            angle = Math.atan2(b.y - lastCp.y, b.x - lastCp.x);
          } else {
            angle = Math.atan2(b.y - a.y, b.x - a.x);
          }
          ctx.beginPath();
          ctx.moveTo(b.x, b.y);
          ctx.lineTo(
            b.x - ARROW_HEAD * Math.cos(angle - 0.4),
            b.y - ARROW_HEAD * Math.sin(angle - 0.4),
          );
          ctx.lineTo(
            b.x - ARROW_HEAD * Math.cos(angle + 0.4),
            b.y - ARROW_HEAD * Math.sin(angle + 0.4),
          );
          ctx.closePath();
          ctx.fill();
        }
      }
    }

    // Vertex dots (only for polygons with > 2 vertices, not circles)
    if (block.shapeKind !== 'circle' && block.shapeKind !== 'ellipse'
        && block.shapeKind !== 'point' && verts.length > 2) {
      for (const v of verts) {
        drawNaturalDot(ctx, v.x, v.y, VERTEX_RADIUS);
        ctx.fillStyle = POINT_FILL;
        ctx.fill();
        ctx.strokeStyle = POINT_BORDER;
        ctx.lineWidth = 0.6;
        ctx.stroke();
      }
      // Restore stroke style for any subsequent drawing
      ctx.strokeStyle = STROKE_COLOR;
      ctx.lineWidth = LINE_WIDTH;
    }

    // ── Vertex drag handles (shown only when figure is selected)
    if (selected && block.shapeKind !== 'circle' && block.shapeKind !== 'ellipse') {
      const handleVerts = block.shapeKind === 'point' && block.center
        ? [block.center]
        : verts;
      const HANDLE_R = VERTEX_RADIUS * 2.5;
      ctx.strokeStyle = 'rgba(50, 40, 30, 0.18)';
      ctx.lineWidth = 0.5;
      for (const v of handleVerts) {
        ctx.beginPath();
        ctx.arc(v.x, v.y, HANDLE_R, 0, Math.PI * 2);
        ctx.stroke();
      }
    } else if (selected && block.center) {
      // Circle/ellipse: center handle
      const HANDLE_R = 4;
      ctx.strokeStyle = 'rgba(50, 40, 30, 0.18)';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.arc(block.center.x, block.center.y, HANDLE_R, 0, Math.PI * 2);
      ctx.stroke();
    }

    // ── Control-point handles (shown when selected figure has curves) ────
    if (selected) {
      const CP_R = 2.2 * scale;
      for (const edge of block.edges) {
        if (!edge.controlPoints || edge.controlPoints.length === 0) continue;
        const a = verts[edge.from];
        const b = verts[edge.to];
        if (!a || !b) continue;
        const cps = edge.controlPoints;
        // Tangent lines (vertex → its associated CP)
        ctx.beginPath();
        ctx.setLineDash([2 * scale, 2 * scale]);
        ctx.strokeStyle = 'rgba(90, 70, 50, 0.25)';
        ctx.lineWidth = 0.5;
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(cps[0].x, cps[0].y);
        if (cps.length >= 2) {
          ctx.moveTo(b.x, b.y);
          ctx.lineTo(cps[1].x, cps[1].y);
        }
        ctx.stroke();
        ctx.setLineDash([]);
        // Diamond handles for each CP
        for (const cp of cps) {
          ctx.beginPath();
          ctx.moveTo(cp.x, cp.y - CP_R);
          ctx.lineTo(cp.x + CP_R, cp.y);
          ctx.lineTo(cp.x, cp.y + CP_R);
          ctx.lineTo(cp.x - CP_R, cp.y);
          ctx.closePath();
          ctx.fillStyle = 'rgba(90, 70, 50, 0.35)';
          ctx.fill();
          ctx.strokeStyle = 'rgba(50, 40, 30, 0.5)';
          ctx.lineWidth = 0.6;
          ctx.stroke();
        }
      }
    }

    ctx.restore();
  }

  // ── LaTeX block ────────────────────────────────────────────────────────────

  private _drawLatexBlock(
    block: import('./notebook_state').WvLatexBlock,
    selected: boolean,
    ctx: CanvasRenderingContext2D,
    t: number,
  ): void {
    const { bbox } = block;

    if (block.latexSuggestion && !block.focusMode) {
      // Ghost suggestion — sepia tinted semi-transparent overlay with subtle pulse
      const pulse = 0.35 + 0.10 * Math.sin(t * 2.2);
      ctx.save();
      ctx.globalAlpha = pulse;
      ctx.fillStyle = 'rgba(220, 200, 160, 0.3)';
      ctx.fillRect(bbox.x, bbox.y, bbox.width, bbox.height);
      ctx.font = `italic ${Math.min(14, bbox.height * 0.4)}px Georgia, serif`;
      ctx.fillStyle = 'rgba(90, 60, 30, 0.80)';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillText(
        truncate(block.latexSuggestion, 28),
        bbox.x + bbox.width / 2,
        bbox.y + bbox.height / 2,
      );
      ctx.restore();
    }

    if (block.focusMode) {
      // Focused latex — bright editing overlay
      ctx.save();
      ctx.fillStyle = 'rgba(255, 252, 240, 0.92)';
      ctx.fillRect(bbox.x, bbox.y, bbox.width, bbox.height);
      ctx.strokeStyle = 'rgba(160, 120, 60, 0.7)';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(bbox.x, bbox.y, bbox.width, bbox.height);
      ctx.font = `16px "Courier New", monospace`;
      ctx.fillStyle = '#3a2a10';
      ctx.textBaseline = 'top';
      ctx.fillText(
        block.latexConfirmed ?? block.latexSuggestion ?? '',
        bbox.x + 6, bbox.y + 6,
      );
      ctx.restore();
    }

    if (block.latexConfirmed && !block.focusMode) {
      // Confirmed — prefer KaTeX-typeset DOM overlay if available.
      const overlay = getLatexOverlay();
      if (overlay) {
        // Transform page-local bbox to viewport (screen) space via the current
        // ctx transform, so the DOM layer (above the canvas) lines up exactly.
        const m = ctx.getTransform();
        const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
        // ctx matrix is in device pixels; divide by dpr to get CSS pixels
        const sx = (m.a * bbox.x + m.c * bbox.y + m.e) / dpr;
        const sy = (m.b * bbox.x + m.d * bbox.y + m.f) / dpr;
        const sw = (m.a * bbox.width) / dpr;
        const sh = (m.d * bbox.height) / dpr;
        overlay.track(block.id, { x: sx, y: sy, w: sw, h: sh }, block.latexConfirmed);

        // Gold ✓ badge on the canvas (above the overlay would need z-index, so
        // just stamp it on the canvas — it's decorative).
        ctx.save();
        ctx.font = '9px Georgia';
        ctx.fillStyle = 'rgba(160, 100, 20, 0.6)';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText('✓', bbox.x + bbox.width - 12, bbox.y + 2);
        ctx.restore();
      } else {
        // Fallback: muted monospace source
        ctx.save();
        const fontSize = Math.min(14, Math.max(10, bbox.height * 0.42));
        ctx.font = `${fontSize}px "Courier New", monospace`;
        ctx.fillStyle = 'rgba(70, 45, 15, 0.78)';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        ctx.fillText(
          truncate(block.latexConfirmed, Math.max(8, Math.floor(bbox.width / (fontSize * 0.6)))),
          bbox.x + 6,
          bbox.y + bbox.height / 2,
        );
        ctx.font = '9px Georgia';
        ctx.fillStyle = 'rgba(160, 100, 20, 0.6)';
        ctx.fillText('✓', bbox.x + bbox.width - 12, bbox.y + 2);
        ctx.restore();
      }
    }

    if (selected) { this._drawInkBlock(bbox, true, ctx); }
  }

  // ── Code block ─────────────────────────────────────────────────────────────

  private _drawCodeBlock(
    block: import('./notebook_state').WvCodeBlock,
    selected: boolean,
    ctx: CanvasRenderingContext2D,
    t: number,
  ): void {
    const { bbox } = block;
    const gap = CODE_VOID_GAP;

    // Void gap (outer ring)
    this._drawVoidBorder(bbox, gap, 'code', 0, ctx);

    // Dark content area
    ctx.save();
    ctx.fillStyle = 'rgba(14, 12, 18, 0.92)';
    ctx.fillRect(bbox.x, bbox.y, bbox.width, bbox.height);

    // State indicator stripe (left edge)
    const stateColors: Record<string, string> = {
      idle: '#4a4060', running: '#50c87a', done: '#6a9fc0', error: '#cc3333',
    };
    ctx.fillStyle = stateColors[block.state] ?? '#4a4060';
    ctx.fillRect(bbox.x + 2, bbox.y + 2, 3, bbox.height - 4);

    // File reference label
    ctx.font = '10px "Courier New", monospace';
    ctx.fillStyle = 'rgba(140, 200, 160, 0.7)';
    ctx.textBaseline = 'top';
    ctx.fillText(block.fileRef || '···', bbox.x + 12, bbox.y + 4);

    // Running pulse
    if (block.state === 'running') {
      const pulse = 0.5 + 0.5 * Math.sin(t * 6);
      ctx.fillStyle = `rgba(80, 200, 120, ${pulse * 0.15})`;
      ctx.fillRect(bbox.x, bbox.y, bbox.width, bbox.height);
    }

    // Outputs (last 2 lines, bottom of block)
    if (block.outputs.length > 0) {
      const lastOut = block.outputs[block.outputs.length - 1];
      if (lastOut.type === 'stream' || lastOut.type === 'text') {
        const y = bbox.y + bbox.height - 18;
        ctx.font = '9px "Courier New", monospace';
        ctx.fillStyle = lastOut.type === 'text'
          ? 'rgba(160, 200, 160, 0.8)'
          : 'rgba(240, 80, 80, 0.8)';
        ctx.fillText(
          truncate(String(lastOut.data), 48),
          bbox.x + 10, y,
        );
      }
    }

    ctx.restore();

    if (selected) {
      ctx.save();
      ctx.strokeStyle = FAMILY_COLORS.invoke;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(bbox.x - gap, bbox.y - gap, bbox.width + gap * 2, bbox.height + gap * 2);
      ctx.restore();
    }
  }

  // ── Arcane channel block ───────────────────────────────────────────────────

  private _drawArcaneBlock(
    block: import('./notebook_state').WvArcaneBlock,
    selected: boolean,
    ctx: CanvasRenderingContext2D,
    t: number,
  ): void {
    const { bbox, arcaneState, glyphSeed, activated, processing } = block;

    // ── Dismiss fade-out ──
    // If the sigil is being dismissed, compute a 0→1 fade progress over 1.5s
    // and scale-shrink the sigil. Skip rendering once fully faded.
    let dismissAlpha = 1;
    let dismissScale = 1;
    if (block.dismissing && block.dismissAt) {
      const elapsed = this.frameNow - block.dismissAt;
      const progress = Math.min(elapsed / 1500, 1);
      dismissAlpha = 1 - progress;
      dismissScale = 1 - progress * 0.4;  // shrink to 60%
      if (dismissAlpha <= 0) return;
    }

    // Use movement position if available, else fall back to bbox center
    const baseCx = block.moveX ?? (bbox.x + bbox.width / 2);
    const baseCy = block.moveY ?? (bbox.y + bbox.height / 2);

    // Floaty bob animation (unless sitting)
    // When processing: add a distinct wobble (fast lateral oscillation)
    const isSitting = block.moveState === 'sitting';
    const procWobbleX = processing ? Math.sin(t * 12 + (glyphSeed ?? 0) * 2.1) * 2.5 : 0;
    const procWobbleY = processing ? Math.cos(t * 10 + (glyphSeed ?? 0) * 1.7) * 1.2 : 0;
    const floatY = isSitting ? 0 : Math.sin(t * 0.8 + (glyphSeed ?? 0) * 1.3) * 3.5;
    const floatX = isSitting ? 0 : Math.sin(t * 0.55 + (glyphSeed ?? 0) * 0.9) * 1.5;

    const cx = baseCx + floatX + procWobbleX;
    const cy = baseCy + floatY + procWobbleY;
    const r = Math.min(bbox.width, bbox.height) * 0.38;
    const seed = glyphSeed ?? 0;

    const isLaura = block.arcaneType === 'laura_channel';
    const isNpc = block.arcaneType === 'laura_channel' || block.arcaneType === 'ireth_channel';

    // Laura = 3-point star (triangle), Ireth = 5-point star (pentagon)
    const arms = isLaura ? 3 : 5;
    const innerR = r * (isLaura ? 0.55 : 0.45);

    // ── Persona-specific colours ──
    const col = isLaura
      ? { base: [180, 120, 60], accent: [200, 140, 60], shadow: [100, 60, 20] }
      : { base: [75, 0, 130], accent: [120, 80, 200], shadow: [30, 0, 60] };

    // ── Animation parameters driven by state ──
    // Persona identity encoded in rhythms: Ireth = slower, angular pulse;
    // Laura = faster, flowing breathe.  Processing = rapid jitter.
    const personaBias = isLaura ? 1.3 : 0.8;
    let shimmerSpeed = 0.4;     // dormant: very slow
    let shimmerAmp   = 0.08;
    let rotSpeed     = 0.01;
    let glowAlpha    = 0;
    let lineAlpha    = 0.55;
    let shadowBlur   = 0;

    if (activated && isNpc) {
      shimmerSpeed = 1.6 * personaBias;
      shimmerAmp   = 0.18;
      rotSpeed     = 0.05 * personaBias;
      glowAlpha    = 0.18;
      lineAlpha    = 0.85;
      shadowBlur   = 6;
    }
    if (processing) {
      shimmerSpeed = 4.0;
      shimmerAmp   = 0.3;
      rotSpeed     = 0.15;
      glowAlpha    = 0.25;
    }
    if (arcaneState === 'sealed') {
      lineAlpha *= 0.6;
      shimmerAmp *= 0.5;
    }

    const breathe = (1 - shimmerAmp) + shimmerAmp * Math.sin(t * shimmerSpeed);
    const rotation = t * rotSpeed + seed * 0.7;

    ctx.save();

    // Apply dismiss fade + shrink transform
    if (dismissAlpha < 1) {
      ctx.globalAlpha = dismissAlpha;
      ctx.translate(cx, cy);
      ctx.scale(dismissScale, dismissScale);
      ctx.translate(-cx, -cy);
    }

    // ═══════════════════════════════════════════════════════════════════════
    // 1. DARK SHADOW GLOW — astral separation from parchment
    // ═══════════════════════════════════════════════════════════════════════
    if (shadowBlur > 0 || activated) {
      const blur = processing ? 10 : shadowBlur;
      const sAlpha = activated ? 0.25 * breathe : 0.08;
      ctx.save();
      ctx.shadowColor = `rgba(${col.shadow[0]}, ${col.shadow[1]}, ${col.shadow[2]}, ${sAlpha})`;
      ctx.shadowBlur = blur;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;
      // Draw an invisible filled circle just to cast the shadow
      ctx.fillStyle = 'rgba(0,0,0,0)';
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, r * 0.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // ═══════════════════════════════════════════════════════════════════════
    // 2. RADIAL GLOW (activated only)
    // ═══════════════════════════════════════════════════════════════════════
    if (glowAlpha > 0) {
      const glowR = r * 1.5;
      const grad = ctx.createRadialGradient(cx, cy, r * 0.15, cx, cy, glowR);
      grad.addColorStop(0, `rgba(${col.accent[0]}, ${col.accent[1]}, ${col.accent[2]}, ${glowAlpha})`);
      grad.addColorStop(1, `rgba(${col.accent[0]}, ${col.accent[1]}, ${col.accent[2]}, 0)`);
      const pulse = 0.6 + 0.4 * Math.sin(t * shimmerSpeed * 1.1);
      ctx.globalAlpha = pulse;
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, glowR, 0, Math.PI * 2);
      ctx.fill();
    }

    // ═══════════════════════════════════════════════════════════════════════
    // 3. OUTER RING with standing arc-end animation
    // ═══════════════════════════════════════════════════════════════════════
    ctx.globalAlpha = lineAlpha * breathe;
    ctx.strokeStyle = `rgba(${col.base[0]}, ${col.base[1]}, ${col.base[2]}, 0.8)`;

    if (activated && isNpc) {
      // Broken arc with animated gap — the arc ends drift and breathe
      const gapAngle = 0.35 + 0.15 * Math.sin(t * shimmerSpeed * 0.7);
      const gapCenter = t * 0.3 * personaBias;  // gap rotates slowly
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(cx, cy, r, gapCenter + gapAngle, gapCenter + Math.PI * 2 - gapAngle);
      ctx.stroke();
      // Small decorative ticks at arc ends
      for (const endAngle of [gapCenter + gapAngle, gapCenter + Math.PI * 2 - gapAngle]) {
        const ex = cx + Math.cos(endAngle) * r;
        const ey = cy + Math.sin(endAngle) * r;
        const perpA = endAngle + Math.PI / 2;
        const tickL = 3 + 2 * Math.sin(t * shimmerSpeed * 1.5);
        ctx.beginPath();
        ctx.moveTo(ex - Math.cos(perpA) * tickL, ey - Math.sin(perpA) * tickL);
        ctx.lineTo(ex + Math.cos(perpA) * tickL, ey + Math.sin(perpA) * tickL);
        ctx.stroke();
      }
    } else {
      // Dormant: complete faint circle
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
    }

    // ═══════════════════════════════════════════════════════════════════════
    // 4. INNER GEOMETRY — arms with dark-glow shadow on each stroke
    // ═══════════════════════════════════════════════════════════════════════
    ctx.strokeStyle = `rgba(${col.accent[0]}, ${col.accent[1]}, ${col.accent[2]}, 0.8)`;
    ctx.lineWidth = activated ? 0.8 : 0.5;

    // Apply dark shadow to geometry strokes when activated
    if (activated) {
      ctx.shadowColor = `rgba(${col.shadow[0]}, ${col.shadow[1]}, ${col.shadow[2]}, 0.35)`;
      ctx.shadowBlur = 3;
    }

    for (let i = 0; i < arms; i++) {
      const angle = rotation + (i * Math.PI * 2) / arms;
      // Processing: arms jitter slightly
      const jitter = processing ? 0.05 * Math.sin(t * 12 + i * 2.3) : 0;
      const tipX = cx + Math.cos(angle + jitter) * innerR;
      const tipY = cy + Math.sin(angle + jitter) * innerR;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(tipX, tipY);
      ctx.stroke();
      // Perpendicular tick at tip
      const perpAngle = angle + jitter + Math.PI / 2;
      const tickLen = r * (activated ? 0.14 : 0.12);
      ctx.beginPath();
      ctx.moveTo(tipX - Math.cos(perpAngle) * tickLen, tipY - Math.sin(perpAngle) * tickLen);
      ctx.lineTo(tipX + Math.cos(perpAngle) * tickLen, tipY + Math.sin(perpAngle) * tickLen);
      ctx.stroke();
    }

    // Connecting polygon at arm tips
    ctx.beginPath();
    for (let i = 0; i <= arms; i++) {
      const angle = rotation + (i * Math.PI * 2) / arms;
      const jitter = processing ? 0.05 * Math.sin(t * 12 + i * 2.3) : 0;
      const px = cx + Math.cos(angle + jitter) * innerR;
      const py = cy + Math.sin(angle + jitter) * innerR;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.stroke();

    // Clear shadow for remaining draws
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    // ═══════════════════════════════════════════════════════════════════════
    // 5. CENTER DOT
    // ═══════════════════════════════════════════════════════════════════════
    const dotR = activated ? 3 : 2;
    ctx.fillStyle = `rgba(${col.accent[0]}, ${col.accent[1]}, ${col.accent[2]}, 0.9)`;
    ctx.globalAlpha = (activated ? 0.85 : 0.5) * breathe;
    ctx.beginPath();
    ctx.arc(cx, cy, dotR, 0, Math.PI * 2);
    ctx.fill();

    // ═══════════════════════════════════════════════════════════════════════
    // 6. EPHEMERAL "SAY" DIALOGUE (cursive text next to sigil)
    // ═══════════════════════════════════════════════════════════════════════
    if (block.ephemeralSay) {
      const age = block.ephemeralSayAt ? ((Date.now() - block.ephemeralSayAt) / 1000) : 0;
      const fadeOut = age > 90 ? Math.max(0, 1 - (age - 90) / 10) : 1;
      if (fadeOut > 0) {
        ctx.save();
        const font = isLaura
          ? '13px "Caveat", cursive'
          : '12px "Homemade Apple", cursive';
        ctx.font = font;
        ctx.fillStyle = `rgba(${col.accent[0]}, ${col.accent[1]}, ${col.accent[2]}, 0.85)`;
        ctx.globalAlpha = 0.8 * fadeOut * breathe;
        ctx.textBaseline = 'top';

        // Position: to the right of the sigil, or below if near right edge
        const sayX = cx + r + 14;
        const sayY = cy - r * 0.5;
        const maxW = 220;
        const lineH = 18;

        // Word-wrap
        const words = block.ephemeralSay.split(' ');
        let line = '';
        let yOff = 0;
        for (const word of words) {
          const test = line ? `${line} ${word}` : word;
          if (ctx.measureText(test).width > maxW && line) {
            ctx.fillText(line, sayX, sayY + yOff);
            yOff += lineH;
            line = word;
          } else {
            line = test;
          }
        }
        if (line) ctx.fillText(line, sayX, sayY + yOff);
        ctx.restore();
      }
    }

    ctx.restore();

    // Selection indicator (only outside fold — fold sigils aren't selectable)
    if (selected) {
      ctx.save();
      ctx.strokeStyle = FAMILY_COLORS.invoke;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.arc(cx, cy, r + 8, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }
  }

  // ── Text block ─────────────────────────────────────────────────────────────

  private _drawTextBlock(
    block: import('./notebook_state').WvTextBlock,
    selected: boolean,
    ctx: CanvasRenderingContext2D,
  ): void {
    const { bbox } = block;
    const style = (block as any).textStyle as { fontFamily?: string; bold?: boolean; italic?: string; underline?: boolean; color?: string; agentId?: string } | undefined;
    const isAgent = !!(style?.agentId) || block.id.startsWith('agent_');
    const isNarrator = block.id.startsWith('fold_') && block.id.endsWith('_text')
      || block.id === 'user_arcane_label';
    ctx.save();

    if (isAgent) {
      // Agent-origin text: no frame, subtle parchment tint, cursive font
      const isIreth = style?.agentId === 'ireth' || style?.color === '#4B0082'
        || (typeof style === 'string' && style === 'ireth');
      ctx.globalAlpha = 0.06;
      ctx.fillStyle = isIreth ? '#6040a0' : '#a07030';
      ctx.fillRect(bbox.x, bbox.y, bbox.width, bbox.height);
      ctx.globalAlpha = 1;

      const family = isIreth ? '"Homemade Apple", cursive' : '"Caveat", cursive';
      const color = isIreth ? 'rgba(75, 0, 130, 0.80)' : 'rgba(107, 66, 38, 0.80)';
      ctx.font = `${block.fontSize || 14}px ${family}`;
      ctx.fillStyle = color;
      ctx.textBaseline = 'top';
      wrapText(ctx, block.content, bbox.x + 6, bbox.y + 5, bbox.width - 12, (block.fontSize || 14) + 6);
    } else if (isNarrator) {
      // Narrator/system text: no frame, elegant serif, calligraphic delimiters
      const fontSize = block.fontSize || 14;
      const lineH = fontSize + 5;
      ctx.font = `italic ${fontSize}px "Palatino Linotype", "Book Antiqua", Palatino, serif`;
      ctx.fillStyle = style?.color ?? 'rgba(80, 55, 30, 0.65)';
      ctx.textBaseline = 'top';

      // Calligraphic delimiter above
      ctx.save();
      ctx.globalAlpha = 0.25;
      ctx.strokeStyle = 'rgba(80, 55, 30, 0.4)';
      ctx.lineWidth = 0.6;
      const delimW = Math.min(bbox.width * 0.5, 120);
      const dcx = bbox.x + bbox.width / 2;
      // Top flourish: a gentle S-curve
      ctx.beginPath();
      ctx.moveTo(dcx - delimW / 2, bbox.y);
      ctx.bezierCurveTo(dcx - delimW / 4, bbox.y - 4, dcx + delimW / 4, bbox.y + 4, dcx + delimW / 2, bbox.y);
      ctx.stroke();
      ctx.restore();

      // Text content
      wrapText(ctx, block.content, bbox.x + 6, bbox.y + 8, bbox.width - 12, lineH);

      // Bottom flourish
      const textLines = this._wrapLines(ctx, block.content || '', bbox.width - 12);
      const textBottom = bbox.y + 8 + textLines.length * lineH + 4;
      ctx.save();
      ctx.globalAlpha = 0.25;
      ctx.strokeStyle = 'rgba(80, 55, 30, 0.4)';
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(dcx - delimW / 2, textBottom);
      ctx.bezierCurveTo(dcx - delimW / 4, textBottom + 4, dcx + delimW / 4, textBottom - 4, dcx + delimW / 2, textBottom);
      ctx.stroke();
      ctx.restore();
    } else {
      // Normal text block: hand-drawn frame unless explicitly disabled.
      // Primary (driver-injected file body) blocks never get a frame — the
      // content IS the page and the frame would just be decorative cruft.
      const explicitlyHidden = (style as { showFrame?: boolean } | undefined)?.showFrame === false;
      const isPrimaryBody = (block as unknown as { _role?: string })._role === 'primary';
      const showFrame = !explicitlyHidden && !isPrimaryBody;
      if (showFrame) {
        ctx.strokeStyle = 'rgba(90, 60, 30, 0.25)';
        ctx.lineWidth = 0.8;
        this._drawSketchRect(ctx, bbox.x, bbox.y, bbox.width, bbox.height, block.id);
      }

      // Choose display mode: handwritten ink or OCR text
      const showInk = block.displayMode !== 'ocr'
        && block.inkWords != null && block.inkWords.length > 0;

      if (showInk) {
        // Render stored handwritten strokes, transformed from srcBBox → dstBBox
        for (const iw of block.inkWords!) {
          const pts = iw.points;
          if (pts.length < 2) continue;
          const src = iw.srcBBox;
          const dst = iw.dstBBox;
          const sx = src.w > 0 ? dst.w / src.w : 1;
          const sy = src.h > 0 ? dst.h / src.h : 1;
          ctx.save();
          ctx.translate(dst.x, dst.y);
          ctx.scale(sx, sy);
          ctx.translate(-src.x, -src.y);
          ctx.strokeStyle = iw.color;
          ctx.lineWidth = iw.size;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(pts[0].x, pts[0].y);
          for (let i = 1; i < pts.length; i++) {
            ctx.lineTo(pts[i].x, pts[i].y);
          }
          ctx.stroke();
          ctx.restore();
        }
      } else {
        // OCR text rendering
        const family = style?.fontFamily ?? 'Georgia, serif';
        const weight = style?.bold ? 'bold' : 'normal';
        const slant  = style?.italic ? 'italic' : 'normal';
        const color = style?.color ?? 'rgba(50, 35, 15, 0.88)';

        // Markdown routing: a block marked as a primary payload and that
        // isn't a code block gets the markdown renderer (headings,
        // checkboxes, inline bold/italic/code).  Fallback: the plain wrap.
        const raw = block as unknown as Record<string, unknown>;
        const isPrimary = raw._role === 'primary';
        const isCode = typeof raw._language === 'string';
        const renderAsMd = isPrimary && !isCode;

        if (renderAsMd) {
          this._drawMarkdownBlock(
            ctx, block, bbox, block.fontSize, color, family,
          );
        } else {
          ctx.font = `${slant} ${weight} ${block.fontSize}px ${family}`;
          ctx.fillStyle = color;
          ctx.textBaseline = 'top';
          wrapText(ctx, block.content, bbox.x + 6, bbox.y + 5, bbox.width - 12, block.fontSize + 4);
        }

        // Underline: draw lines under each text line if enabled
        if (style?.underline && block.content) {
          const lineH = block.fontSize + 4;
          const lines = this._wrapLines(ctx, block.content, bbox.width - 12);
          ctx.strokeStyle = style?.color ?? 'rgba(50, 35, 15, 0.6)';
          ctx.lineWidth = 0.7;
          for (let i = 0; i < lines.length; i++) {
            const ly = bbox.y + 5 + i * lineH + block.fontSize + 1;
            const lw = Math.min(ctx.measureText(lines[i]).width, bbox.width - 12);
            ctx.beginPath();
            ctx.moveTo(bbox.x + 6, ly);
            ctx.lineTo(bbox.x + 6 + lw, ly);
            ctx.stroke();
          }
        }
      }
    }

    ctx.restore();
    if (selected) { this._drawInkBlock(bbox, true, ctx); }
  }

  /** Draw a slightly wobbly rectangle (hand-drawn feel). */
  private _drawSketchRect(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number,
    seed: string,
  ): void {
    // Simple deterministic jitter from the block id
    let hash = 0;
    for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
    const j = (i: number) => ((Math.sin(hash * 0.3 + i * 7.13) + 1) * 0.5 - 0.5) * 1.6;

    ctx.beginPath();
    ctx.moveTo(x + j(0), y + j(1));
    ctx.lineTo(x + w + j(2), y + j(3));
    ctx.lineTo(x + w + j(4), y + h + j(5));
    ctx.lineTo(x + j(6), y + h + j(7));
    ctx.closePath();
    ctx.stroke();
  }

  // ── Markdown block renderer ───────────────────────────────────────────────
  //
  // Primary (file-body) text blocks get this path. Renders the full
  // content as markdown (headings, lists, checkboxes, inline bold /
  // italic / code), clipped to the block's bbox. No pagination —
  // overflow lines beyond the bbox are clipped; we'll layer a
  // proper sheet-pager on top later, ideally driven by scroll rather
  // than the previous greedy-pack scheme that miscounted lines.
  private _drawMarkdownBlock(
    ctx: CanvasRenderingContext2D,
    block: import('./notebook_state').WvTextBlock,
    bbox: { x: number; y: number; width: number; height: number },
    baseSize: number,
    color: string,
    family: string,
  ): void {
    const contentTopPad = 5;
    const availW = bbox.width - 12;
    const blk = block as unknown as { content: string };

    ctx.save();
    ctx.beginPath();
    ctx.rect(bbox.x, bbox.y, bbox.width, bbox.height);
    ctx.clip();
    renderMarkdown(
      ctx, blk.content ?? '',
      bbox.x + 6, bbox.y + contentTopPad,
      availW,
      baseSize,
      color,
      family,
    );
    ctx.restore();
  }

  /** Simple word-wrap into lines (mirrors wrapText but returns array). */
  private _wrapLines(
    ctx: CanvasRenderingContext2D, text: string, maxW: number,
  ): string[] {
    const lines: string[] = [];
    const paragraphs = (text ?? '').split('\n');
    for (const paragraph of paragraphs) {
      if (paragraph.length === 0) {
        lines.push('');
        continue;
      }
      const words = paragraph.split(' ');
      let line = '';
      for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > maxW && line) {
          lines.push(line);
          line = w;
        } else { line = test; }
      }
      if (line) lines.push(line);
    }
    return lines;
  }

  // ── Void border (shared by code + arcane) ─────────────────────────────────

  private _drawVoidBorder(
    bbox: { x: number; y: number; width: number; height: number },
    gap: number,
    mode: 'code' | 'arcane',
    seed: number,
    ctx: CanvasRenderingContext2D,
  ): void {
    ctx.save();
    const ox = bbox.x - gap, oy = bbox.y - gap;
    const ow = bbox.width + gap * 2, oh = bbox.height + gap * 2;

    // Dark void ring
    ctx.fillStyle = VOID_DARK;
    // Draw as a frame (4 rects to avoid obscuring content)
    ctx.fillRect(ox, oy, ow, gap);                   // top
    ctx.fillRect(ox, bbox.y + bbox.height, ow, gap); // bottom
    ctx.fillRect(ox, oy, gap, oh);                   // left
    ctx.fillRect(bbox.x + bbox.width, oy, gap, oh);  // right

    // Torn-edge effect — tiny irregular shadow on inner edge
    const shadow = ctx.createLinearGradient(bbox.x, bbox.y - gap, bbox.x, bbox.y);
    shadow.addColorStop(0, VOID_DARK);
    shadow.addColorStop(0.6, 'rgba(14, 12, 18, 0.6)');
    shadow.addColorStop(1, 'transparent');
    ctx.fillStyle = shadow;
    ctx.fillRect(bbox.x, bbox.y - gap, bbox.width, gap);

    // Arcane: rotating glyphs in the border gap
    if (mode === 'arcane') {
      const glyphSubset = pickGlyphs(seed, 6);
      const totalPerimeter = 2 * (bbox.width + bbox.height);
      const glyphSpacing = totalPerimeter / glyphSubset.length;
      const rotSpeed = 0.02 + ((seed % 100) / 2000);  // 0.02 – 0.07 rad/s
      const now = performance.now() / 1000;
      ctx.font = `${ARCANE_GLYPH_SIZE}px Georgia`;
      ctx.fillStyle = 'rgba(140, 120, 200, 0.6)';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';

      for (let i = 0; i < glyphSubset.length; i++) {
        const t = ((now * rotSpeed + i / glyphSubset.length) % 1);
        const dist = t * totalPerimeter;
        const pos = perimeterPoint(bbox, dist);
        // Nudge into void gap
        const cx = gap / 2, cy = gap / 2;
        const midX = bbox.x + bbox.width / 2;
        const midY = bbox.y + bbox.height / 2;
        const dx = pos.x - midX, dy = pos.y - midY;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const gx = pos.x + (dx / len) * (cx + 1);
        const gy = pos.y + (dy / len) * (cy + 1);
        ctx.fillText(glyphSubset[i], gx, gy);
      }
    }

    ctx.restore();
  }

  // ── Relation arrows ──────────────────────────────────────────────────────

  private _drawRelationArrow(
    arrow: WvRelationArrow,
    ctx: CanvasRenderingContext2D,
    _t: number,
  ): void {
    const style = RELATION_STYLE[arrow.type] ?? RELATION_STYLE.references;

    // Compute edge midpoints
    const from = arrow.fromBbox;
    const to = arrow.toBbox;
    const fx = from.x + from.width / 2;
    const fy = from.y + from.height / 2;
    const tx = to.x + to.width / 2;
    const ty = to.y + to.height / 2;

    // Bezier control point — perpendicular offset for a gentle curve
    const mx = (fx + tx) / 2;
    const my = (fy + ty) / 2;
    const dx = tx - fx, dy = ty - fy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const offset = Math.min(len * 0.25, 40);
    const cx = mx + (-dy / len) * offset;
    const cy = my + (dx / len) * offset;

    ctx.save();
    ctx.strokeStyle = style.color;
    ctx.lineWidth = arrow.confirmed ? 1.5 : 1;
    ctx.setLineDash(arrow.confirmed ? style.dash : [3, 5]);
    ctx.globalAlpha = arrow.confirmed ? 1 : 0.55;

    // Draw curve
    ctx.beginPath();
    ctx.moveTo(fx, fy);
    ctx.quadraticCurveTo(cx, cy, tx, ty);
    ctx.stroke();

    // Arrowhead
    const angle = Math.atan2(ty - cy, tx - cx);
    const headLen = 8;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(tx - headLen * Math.cos(angle - 0.35), ty - headLen * Math.sin(angle - 0.35));
    ctx.moveTo(tx, ty);
    ctx.lineTo(tx - headLen * Math.cos(angle + 0.35), ty - headLen * Math.sin(angle + 0.35));
    ctx.stroke();

    // Label at midpoint
    if (arrow.label || !arrow.confirmed) {
      ctx.font = '9px Georgia';
      ctx.fillStyle = style.color;
      ctx.textBaseline = 'bottom';
      ctx.textAlign = 'center';
      const labelText = arrow.label ?? (arrow.confirmed ? '' : `${style.label}?`);
      if (labelText) {
        ctx.fillText(labelText, cx, cy - 3);
      }
    }

    ctx.restore();
  }

  // ── Marginalia ───────────────────────────────────────────────────────────

  private _drawMarginale(
    note: WvMarginNote,
    ctx: CanvasRenderingContext2D,
    _t: number,
  ): void {
    ctx.save();
    const W = 160;
    const lineHeight = 13;
    const words = note.text.split(' ');
    const lines: string[] = [];
    let current = '';
    ctx.font = 'italic 11px Georgia, serif';
    for (const w of words) {
      const test = current ? `${current} ${w}` : w;
      if (ctx.measureText(test).width > W - 8) { lines.push(current); current = w; }
      else { current = test; }
    }
    if (current) { lines.push(current); }

    const H = lines.length * lineHeight + 8;
    const x = note.x, y = note.y;

    // Parchment note bg
    ctx.fillStyle = 'rgba(245, 235, 210, 0.88)';
    ctx.shadowColor = 'rgba(0,0,0,0.25)';
    ctx.shadowBlur = 6;
    ctx.fillRect(x, y, W, H);

    ctx.shadowBlur = 0;
    // Glyph indicator
    const glyphColor: Record<string, string> = {
      calm: '#408050', warning: '#c08020', archaic: '#5080c0', dismissive: '#c23030',
    };
    ctx.fillStyle = glyphColor[note.glyphStyle] ?? '#805030';
    ctx.font = '9px Georgia';
    ctx.fillText(glyphStyleIcon(note.glyphStyle), x + 2, y + 10);

    // Body text
    ctx.fillStyle = '#3a2510';
    ctx.font = 'italic 11px Georgia, serif';
    ctx.textBaseline = 'top';
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], x + 12, y + 4 + i * lineHeight);
    }

    ctx.restore();
  }

  // ── Selection modifier strip ───────────────────────────────────────────
  // Three pill toggles (lock / group / astral) anchored just below the
  // selection bbox. State reflects "any selected block carries the
  // modifier" — taps in canvas_app's pointer-down path invert that.
  private _drawModifierStrip(
    ctx: CanvasRenderingContext2D,
    bbox: { x: number; y: number; width: number; height: number },
    selection: WvSelection,
    blocks: WvBlock[],
  ): void {
    let anyLocked = false, anyGrouped = false;
    let count = 0;
    for (const id of selection.blockIds) {
      const b = blocks.find(bl => bl.id === id) as
        (import('./notebook_state').WvBaseBlock) | undefined;
      if (!b) continue;
      count++;
      if (b.locked) anyLocked = true;
      if (b.groupId) anyGrouped = true;
    }
    if (count === 0) return;

    const PILL_W = 30, PILL_H = 22, GAP = 4;
    const stripW = PILL_W * 2 + GAP;
    const stripX = bbox.x + bbox.width / 2 - stripW / 2;
    const stripY = bbox.y + bbox.height + 14;

    const pills: Array<{ kind: 'lock' | 'group'; active: boolean; icon: string }> = [
      { kind: 'lock',   active: anyLocked,  icon: '\u{1F512}' },
      { kind: 'group',  active: anyGrouped, icon: '⚭' },
    ];

    ctx.save();
    ctx.font = '13px "Segoe UI Symbol", "Apple Color Emoji", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < pills.length; i++) {
      const p = pills[i];
      const px = stripX + i * (PILL_W + GAP);
      const py = stripY;

      ctx.beginPath();
      const r = PILL_H / 2;
      ctx.moveTo(px + r, py);
      ctx.lineTo(px + PILL_W - r, py);
      ctx.arcTo(px + PILL_W, py, px + PILL_W, py + r, r);
      ctx.lineTo(px + PILL_W, py + PILL_H - r);
      ctx.arcTo(px + PILL_W, py + PILL_H, px + PILL_W - r, py + PILL_H, r);
      ctx.lineTo(px + r, py + PILL_H);
      ctx.arcTo(px, py + PILL_H, px, py + PILL_H - r, r);
      ctx.lineTo(px, py + r);
      ctx.arcTo(px, py, px + r, py, r);
      ctx.closePath();

      if (p.active) {
        ctx.fillStyle = '#e6d4a8';
        ctx.strokeStyle = '#3a2010';
      } else {
        ctx.fillStyle = 'rgba(248, 240, 218, 0.78)';
        ctx.strokeStyle = 'rgba(58, 32, 16, 0.55)';
      }
      ctx.lineWidth = 1.2;
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = p.active ? '#3a2010' : 'rgba(58, 32, 16, 0.6)';
      ctx.fillText(p.icon, px + PILL_W / 2, py + PILL_H / 2 + 1);

      this.modifierStripHits.push({
        kind: p.kind, active: p.active,
        x: px, y: py, w: PILL_W, h: PILL_H,
      });
    }
    ctx.restore();
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function perimeterPoint(
  bbox: { x: number; y: number; width: number; height: number },
  dist: number,
): { x: number; y: number } {
  const { x, y, width: w, height: h } = bbox;
  const perimeter = 2 * (w + h);
  const d = ((dist % perimeter) + perimeter) % perimeter;
  if (d < w)       { return { x: x + d,     y }; }            // top
  if (d < w + h)   { return { x: x + w,     y: y + d - w }; } // right
  if (d < 2 * w + h) { return { x: x + w - (d - w - h), y: y + h }; } // bottom
  return { x, y: y + h - (d - 2 * w - h) };                   // left
}

function pickGlyphs(seed: number, count: number): string[] {
  const result: string[] = [];
  let s = seed;
  for (let i = 0; i < count; i++) {
    s = (s * 1664525 + 1013904223) >>> 0;
    result.push(ARCANE_GLYPHS[s % ARCANE_GLYPHS.length]);
  }
  return result;
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

// ── Markdown inline parsing ──────────────────────────────────────────────────
// Segments within a single logical line, each with its own style flags.
// Supports **bold**, *italic*, `code`.  Not a full CommonMark parser — just
// enough for journal-style prose.

interface MdSegment {
  text: string;
  bold: boolean;
  italic: boolean;
  code: boolean;
}

function parseInlineMd(line: string): MdSegment[] {
  const out: MdSegment[] = [];
  let bold = false;
  let italic = false;
  let code = false;
  let buf = '';
  const flush = () => {
    if (buf) { out.push({ text: buf, bold, italic, code }); buf = ''; }
  };
  let i = 0;
  while (i < line.length) {
    // Inside a code span, only `` closes it — no other markup applies.
    if (code) {
      if (line[i] === '`') { flush(); code = false; i++; continue; }
      buf += line[i]; i++; continue;
    }
    if (line[i] === '`') { flush(); code = true; i++; continue; }
    if (line[i] === '*' && line[i + 1] === '*') {
      flush(); bold = !bold; i += 2; continue;
    }
    // Avoid triggering italic on stand-alone `*` surrounded by spaces/digits
    if (line[i] === '*' && (italic || (i + 1 < line.length && line[i + 1] !== ' '))) {
      flush(); italic = !italic; i++; continue;
    }
    if (line[i] === '_' && (italic || (i + 1 < line.length && /\w/.test(line[i + 1] ?? '')))) {
      // Only treat _ as italic toggle at word-ish boundaries to avoid `file_name.md`
      const prev = line[i - 1] ?? ' ';
      if (!italic && /\w/.test(prev)) { buf += '_'; i++; continue; }
      flush(); italic = !italic; i++; continue;
    }
    buf += line[i]; i++;
  }
  flush();
  return out;
}

/**
 * Render a block of markdown-like content into a rectangle.
 *
 * Handles:
 *   - `#`, `##`, `###`, `####` headings
 *   - `- [ ]` / `- [x]` checkboxes
 *   - `- ` / `* ` bullets
 *   - `---` horizontal rule
 *   - inline `**bold**`, `*italic*`, `` `code` ``
 *
 * Returns the y coordinate just past the last rendered line (useful for
 * computing overflow).
 */
function renderMarkdown(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number, y: number,
  maxW: number,
  baseSize: number,
  color: string,
  family: string,
): number {
  const lines = (text ?? '').split('\n');
  let cy = y;
  for (const raw of lines) {
    cy = renderMarkdownLine(ctx, raw, x, cy, maxW, baseSize, color, family);
  }
  return cy;
}

function renderMarkdownLine(
  ctx: CanvasRenderingContext2D,
  rawLine: string,
  x: number, y: number,
  maxW: number,
  baseSize: number,
  color: string,
  family: string,
): number {
  // Empty → half-line gap
  if (rawLine.trim() === '') {
    return y + (baseSize + 4);
  }

  // Horizontal rule `---`, `***`
  if (/^\s*([-*_])\s*\1\s*\1[\s\1]*$/.test(rawLine)) {
    ctx.save();
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.3;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    const yy = y + baseSize * 0.5;
    ctx.moveTo(x, yy);
    ctx.lineTo(x + maxW, yy);
    ctx.stroke();
    ctx.restore();
    return y + (baseSize + 4);
  }

  // Detect prefix (heading, checkbox, bullet)
  let text = rawLine;
  let size = baseSize;
  let weight: 'normal' | 'bold' = 'normal';
  let checkbox: 'empty' | 'checked' | null = null;
  let isBullet = false;
  let indentSp = 0;
  let prefixAlpha = 1;

  let m: RegExpExecArray | null;
  if ((m = /^(#{1,6})\s+(.*)$/.exec(rawLine))) {
    const level = m[1].length;
    text = m[2];
    weight = 'bold';
    size = Math.round(
      baseSize * (level === 1 ? 1.6 : level === 2 ? 1.35 : level === 3 ? 1.18 : 1.05),
    );
  } else if ((m = /^(\s*)-\s*\[ \]\s+(.*)$/.exec(rawLine))) {
    checkbox = 'empty';
    indentSp = m[1].length;
    text = m[2];
  } else if ((m = /^(\s*)-\s*\[x\]\s+(.*)$/i.exec(rawLine))) {
    checkbox = 'checked';
    indentSp = m[1].length;
    text = m[2];
    prefixAlpha = 0.55;
  } else if ((m = /^(\s*)[-*+]\s+(.*)$/.exec(rawLine))) {
    isBullet = true;
    indentSp = m[1].length;
    text = m[2];
  } else if ((m = /^(\s*)(\d+)\.\s+(.*)$/.exec(rawLine))) {
    // Ordered list — render like bullet with the number as prefix
    indentSp = m[1].length;
    text = m[3];
    isBullet = true;
    // Prepend the number so it renders in-line with prose.
    text = `${m[2]}. ${text}`;
  }

  const indentPx = indentSp * 4; // 4px per space of indent
  let contentX = x + indentPx;
  const lineH = size + 4;

  // Draw checkbox / bullet marker
  if (checkbox !== null) {
    const boxSize = Math.max(10, Math.round(size * 0.7));
    const boxY = y + Math.max(2, Math.round((size - boxSize) * 0.5));
    ctx.save();
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.8;
    ctx.lineWidth = 1.1;
    ctx.strokeRect(contentX, boxY, boxSize, boxSize);
    if (checkbox === 'checked') {
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(contentX + boxSize * 0.18, boxY + boxSize * 0.52);
      ctx.lineTo(contentX + boxSize * 0.42, boxY + boxSize * 0.78);
      ctx.lineTo(contentX + boxSize * 0.82, boxY + boxSize * 0.22);
      ctx.stroke();
    }
    ctx.restore();
    contentX += boxSize + 6;
  } else if (isBullet) {
    ctx.save();
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.75;
    ctx.beginPath();
    ctx.arc(contentX + 3, y + size * 0.55, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    contentX += 10;
  }

  const remainingW = Math.max(20, x + maxW - contentX);

  // Render inline segments with word wrapping.  Continuation lines start at
  // `contentX` (preserves indent for wrapped bullets / checkboxes).
  const segments = parseInlineMd(text);
  const endY = drawInlineSegments(
    ctx, segments, contentX, y, remainingW, size, weight, color, family, prefixAlpha,
  );

  return endY + 2; // small trailing gap between logical lines
}

function drawInlineSegments(
  ctx: CanvasRenderingContext2D,
  segments: MdSegment[],
  startX: number, startY: number,
  maxW: number,
  fontSize: number,
  baseWeight: 'normal' | 'bold',
  color: string,
  family: string,
  alpha: number,
): number {
  if (segments.length === 0) return startY + (fontSize + 4);

  ctx.save();
  ctx.fillStyle = color;
  ctx.globalAlpha = alpha;
  ctx.textBaseline = 'top';

  const lineH = fontSize + 4;
  const rightEdge = startX + maxW;
  let cx = startX;
  let cy = startY;

  const fontFor = (seg: MdSegment): string => {
    const w = seg.bold ? 'bold' : baseWeight;
    const slant = seg.italic ? 'italic' : 'normal';
    const fam = seg.code ? '"Menlo", "Consolas", monospace' : family;
    return `${slant} ${w} ${fontSize}px ${fam}`;
  };

  for (const seg of segments) {
    if (!seg.text) continue;
    ctx.font = fontFor(seg);
    // Break on runs of whitespace, keep the whitespace with the preceding token
    // so wrapping still looks natural.
    const tokens = seg.text.match(/\S+\s*|\s+/g) ?? [];
    for (const tk of tokens) {
      const tkW = ctx.measureText(tk).width;
      if (cx + tkW > rightEdge && cx > startX) {
        cy += lineH;
        cx = startX;
        // Don't render a line-leading pure-whitespace token
        if (/^\s+$/.test(tk)) continue;
      }
      if (seg.code) {
        // Subtle code-span background
        const bgPad = 2;
        const bgH = fontSize + bgPad;
        ctx.save();
        ctx.globalAlpha = alpha * 0.18;
        ctx.fillStyle = color;
        ctx.fillRect(cx - 1, cy - 1, tkW + 2, bgH);
        ctx.restore();
      }
      ctx.fillText(tk, cx, cy);
      cx += tkW;
    }
  }

  ctx.restore();
  return cy + lineH;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number, y: number,
  maxW: number, lineH: number,
): void {
  let iy = y;
  const paragraphs = (text ?? '').split('\n');
  for (const paragraph of paragraphs) {
    if (paragraph.length === 0) {
      iy += lineH;
      continue;
    }
    const words = paragraph.split(' ');
    let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width > maxW && line) {
        ctx.fillText(line, x, iy);
        line = w; iy += lineH;
      } else { line = test; }
    }
    if (line) { ctx.fillText(line, x, iy); iy += lineH; }
  }
}

function glyphStyleIcon(style: string): string {
  const icons: Record<string, string> = {
    calm: '⊕', warning: '↯', archaic: '☿', dismissive: '⊗',
  };
  return icons[style] ?? '·';
}
